// pages/About.jsx
import { Link } from "react-router-dom";
import { 
  FaEye, 
  FaBullseye, 
  FaHeart, 
  FaUsers, 
  FaLeaf, 
  FaGavel, 
  FaHeartbeat, 
  FaLightbulb, 
  FaBalanceScale,
  FaAward,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaQuoteRight,
  FaHandHoldingHeart,
  FaGlobeAfrica,
  FaFemale,
  FaShieldAlt,
  FaSeedling,
  FaArrowRight
} from "react-icons/fa";
import { motion } from "framer-motion";

function About() {
  const values = [
    { name: "Honesty", icon: "🤝", description: "Operating with truth and transparency in all our dealings", color: "from-blue-400 to-blue-600" },
    { name: "Accountability", icon: "✓", description: "Taking responsibility for our actions and commitments", color: "from-green-400 to-green-600" },
    { name: "Integrity", icon: "⭐", description: "Upholding strong moral principles in every decision", color: "from-yellow-400 to-yellow-600" },
    { name: "Transparency", icon: "🔍", description: "Open communication and clear reporting to all stakeholders", color: "from-purple-400 to-purple-600" },
    { name: "Team Work", icon: "👥", description: "Collaborating effectively to achieve common goals", color: "from-red-400 to-red-600" }
  ];

  const pillars = [
    { icon: FaFemale, title: "Gender Equality & Empowerment", color: "from-pink-400 to-pink-600", description: "Promoting women's leadership, rights, and equal opportunities in all spheres of community life.", stats: "2,500+ women empowered" },
    { icon: FaGavel, title: "Good Governance & Inclusion", color: "from-blue-400 to-blue-600", description: "Strengthening participatory decision-making and accountability at all levels.", stats: "15+ community forums held" },
    { icon: FaHeartbeat, title: "Health & Nutrition", color: "from-red-400 to-red-600", description: "Promoting well-being, access to healthcare, and nutrition education for all.", stats: "3,000+ beneficiaries reached" },
    { icon: FaLightbulb, title: "Economic Empowerment & Innovation", color: "from-yellow-400 to-yellow-600", description: "Creating sustainable economic opportunities for youth and women through innovation.", stats: "200+ youth trained" },
    { icon: FaSeedling, title: "Climate Justice & Conservation", color: "from-green-400 to-green-600", description: "Protecting ecosystems, promoting sustainable practices, and addressing climate impacts.", stats: "1,000+ trees planted" }
  ];

  const stats = [
    { number: "5,000+", label: "Beneficiaries Reached", icon: FaUsers },
    { number: "15+", label: "Active Programs", icon: FaHandHoldingHeart },
    { number: "10+", label: "Partner Organizations", icon: FaGlobeAfrica },
    { number: "5", label: "Counties Served", icon: FaMapMarkerAlt }
  ];

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      {/* Hero Section - Fixed responsive buttons */}
      <section className="relative bg-primaryBlue text-white py-16 md:py-32 overflow-hidden">
        {/* Animated Background Elements */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 0.1, scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute top-0 left-0 w-64 h-64 bg-primaryGold rounded-full -translate-x-1/2 -translate-y-1/2"
        />
        <motion.div 
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 0.1, scale: 1 }}
          transition={{ duration: 1.5, delay: 0.3 }}
          className="absolute bottom-0 right-0 w-96 h-96 bg-primaryGold rounded-full translate-x-1/3 translate-y-1/3"
        />
        
        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 0 }}
              animate={{ 
                opacity: [0, 0.5, 0],
                y: [-20, -40],
                x: Math.sin(i) * 20
              }}
              transition={{
                duration: 3,
                delay: i * 0.2,
                repeat: Infinity,
                repeatType: "reverse"
              }}
              className="absolute w-1 h-1 bg-primaryGold/30 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`
              }}
            />
          ))}
        </div>

        <motion.div 
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
        >
          <motion.div variants={fadeInUp} className="text-center">
            <motion.div
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.8 }}
              className="inline-block p-3 md:p-4 bg-primaryGold/20 rounded-full mb-4 md:mb-6"
            >
              <FaHeart className="text-2xl md:text-4xl text-primaryGold" />
            </motion.div>
            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold mb-4 md:mb-6 px-4">
              About Us <span className="text-primaryGold">Rising to Greatness</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl max-w-4xl mx-auto leading-relaxed text-gray-100 px-4 mb-6 md:mb-8">
              A non-profit young women-led Community Based Organization located in Kakamega, Kenya, 
              holistically uplifting marginalized communities and positioning them at the forefront 
              of development and transformative change.
            </p>
            
            {/* Fixed Buttons - Stack on mobile, side by side on desktop */}
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-4 max-w-xs sm:max-w-none mx-auto"
            >
              <motion.div 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.95 }}
                className="w-full sm:w-auto"
              >
                <Link
                  to="/projects"
                  className="block w-full sm:w-auto bg-primaryGold text-primaryBlue px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold hover:bg-yellow-400 transition-all duration-300 shadow-lg hover:shadow-xl text-center text-sm md:text-base"
                >
                  <span className="flex items-center justify-center gap-2">
                    <FaHandHoldingHeart /> Explore Our Work
                  </span>
                </Link>
              </motion.div>
              
              <motion.div 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.95 }}
                className="w-full sm:w-auto"
              >
                <Link
                  to="/contact"
                  className="block w-full sm:w-auto bg-transparent border-2 border-primaryGold text-primaryGold px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold hover:bg-primaryGold/10 transition-all duration-300 text-center text-sm md:text-base"
                >
                  Get Involved
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ scale: 1.05 }}
                className="text-center group"
              >
                <div className="w-16 h-16 md:w-20 md:h-20 bg-primaryGold/10 rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-4 group-hover:bg-primaryGold/20 transition-all duration-300 transform group-hover:rotate-6">
                  <stat.icon className="text-2xl md:text-4xl text-primaryGold" />
                </div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <h3 className="text-2xl md:text-4xl font-bold text-primaryBlue mb-1 md:mb-2">{stat.number}</h3>
                  <p className="text-xs md:text-base text-gray-600">{stat.label}</p>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Vision Mission Section */}
      <section className="py-16 md:py-20 px-4 bg-gradient-to-br from-primaryBlue/5 to-primaryGold/5">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-6 md:gap-8"
          >
            {/* Vision Card */}
            <motion.div 
              variants={fadeInUp}
              whileHover={{ y: -5 }}
              className="bg-white rounded-2xl md:rounded-3xl shadow-xl md:shadow-2xl p-6 md:p-10 transform transition-all duration-500 border-2 border-transparent hover:border-primaryGold/20 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-primaryGold/5 rounded-full -translate-y-16 translate-x-16 group-hover:scale-150 transition-transform duration-700"></div>
              <div className="flex items-center mb-4 md:mb-8">
                <div className="w-12 h-12 md:w-20 md:h-20 bg-gradient-to-br from-primaryGold to-yellow-400 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg transform -rotate-6 group-hover:rotate-0 transition-transform duration-500">
                  <FaEye className="text-2xl md:text-4xl text-white" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-primaryBlue ml-4 md:ml-6">Our Vision</h2>
              </div>
              <p className="text-xl md:text-2xl text-gray-700 leading-relaxed italic">
                "Empowered and Sustainable Communities"
              </p>
              <div className="mt-4 md:mt-8 flex items-center text-primaryGold">
                <div className="w-12 h-1 bg-primaryGold rounded-full"></div>
                <FaQuoteRight className="ml-4 text-xl md:text-2xl" />
              </div>
            </motion.div>

            {/* Mission Card */}
            <motion.div 
              variants={fadeInUp}
              whileHover={{ y: -5 }}
              className="bg-white rounded-2xl md:rounded-3xl shadow-xl md:shadow-2xl p-6 md:p-10 transform transition-all duration-500 border-2 border-transparent hover:border-primaryGold/20 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-primaryGold/5 rounded-full -translate-y-16 translate-x-16 group-hover:scale-150 transition-transform duration-700"></div>
              <div className="flex items-center mb-4 md:mb-8">
                <div className="w-12 h-12 md:w-20 md:h-20 bg-gradient-to-br from-primaryGold to-yellow-400 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg transform rotate-6 group-hover:rotate-0 transition-transform duration-500">
                  <FaBullseye className="text-2xl md:text-4xl text-white" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-primaryBlue ml-4 md:ml-6">Our Mission</h2>
              </div>
              <p className="text-lg md:text-2xl text-gray-700 leading-relaxed italic">
                "Empowering Youth and Women and Positioning them in Realizing Self-Reliance, 
                Just and Sustainable Communities"
              </p>
              <div className="mt-4 md:mt-8 flex items-center text-primaryGold">
                <div className="w-12 h-1 bg-primaryGold rounded-full"></div>
                <FaQuoteRight className="ml-4 text-xl md:text-2xl" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-16 md:py-20 bg-white px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
            className="text-center mb-10 md:mb-16"
          >
            <motion.div variants={fadeInUp}>
              <div className="inline-block p-3 md:p-4 bg-primaryGold/10 rounded-full mb-4">
                <FaHeart className="text-3xl md:text-5xl text-primaryGold" />
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primaryBlue mb-4">Our Core Values</h2>
              <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
                These principles guide every decision we make and every action we take
              </p>
            </motion.div>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6"
          >
            {values.map((value, index) => (
              <motion.div 
                key={value.name}
                variants={fadeInUp}
                whileHover={{ y: -5, scale: 1.02 }}
                className="group relative"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${value.color} rounded-xl md:rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl`}></div>
                <div className="relative bg-white rounded-xl md:rounded-2xl p-4 md:p-8 text-center shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primaryGold to-yellow-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                  <div className="text-4xl md:text-6xl mb-3 md:mb-4 transform group-hover:scale-110 transition-transform duration-500">
                    {value.icon}
                  </div>
                  <h3 className="text-lg md:text-2xl font-bold text-primaryBlue mb-2 md:mb-3">{value.name}</h3>
                  <p className="text-xs md:text-base text-gray-600">{value.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Thematic Pillars Section */}
      <section className="py-16 md:py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
            className="text-center mb-10 md:mb-16"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primaryBlue mb-4">Thematic Pillars</h2>
              <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
                Our work is structured around five key areas of focus, each driving sustainable change
              </p>
            </motion.div>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          >
            {pillars.map((pillar, index) => (
              <motion.div 
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -5, scale: 1.02 }}
                className="group relative"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${pillar.color} rounded-2xl md:rounded-3xl opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                <div className="relative bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-lg md:shadow-xl hover:shadow-2xl transition-all duration-500 border-2 border-transparent hover:border-primaryGold/20 overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-primaryGold/10 to-transparent rounded-full -translate-y-12 translate-x-12 group-hover:scale-150 transition-transform duration-700"></div>
                  <pillar.icon className={`text-4xl md:text-5xl ${pillar.color.replace('from-', 'text-').replace(' to-', '')} mb-4 md:mb-6 transform group-hover:scale-110 transition-transform duration-500`} />
                  <h3 className="text-lg md:text-2xl font-bold text-primaryBlue mb-3 md:mb-4">{pillar.title}</h3>
                  <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">{pillar.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs md:text-sm font-semibold text-primaryGold bg-primaryGold/10 px-3 md:px-4 py-1 md:py-2 rounded-full">
                      {pillar.stats}
                    </span>
                    <motion.div
                      whileHover={{ x: 5 }}
                      className="text-primaryGold"
                    >
                      <FaArrowRight />
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Link to Team Page */}
          <motion.div 
            variants={fadeInUp}
            className="text-center mt-10 md:mt-12"
          >
            <Link
              to="/team"
              className="inline-flex items-center gap-2 text-primaryBlue hover:text-primaryGold transition-colors duration-300 text-base md:text-lg font-semibold group"
            >
              <FaUsers className="text-xl md:text-2xl" />
              Meet Our Dedicated Team
              <FaArrowRight className="group-hover:translate-x-2 transition-transform duration-300" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Call to Action - Fixed responsive buttons */}
      <section className="py-16 md:py-20 px-4 bg-gradient-to-r from-primaryBlue to-primaryGold relative overflow-hidden">
        {/* Animated Background */}
        <motion.div 
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0]
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse"
          }}
          className="absolute top-0 left-0 w-48 md:w-64 h-48 md:h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"
        />
        <motion.div 
          animate={{ 
            scale: [1, 1.5, 1],
            rotate: [0, -90, 0]
          }}
          transition={{ 
            duration: 15,
            repeat: Infinity,
            repeatType: "reverse"
          }}
          className="absolute bottom-0 right-0 w-64 md:w-96 h-64 md:h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3"
        />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6 px-4"
            >
              Join Us in Making a Difference
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-base sm:text-lg md:text-xl text-white/90 mb-6 md:mb-10 leading-relaxed px-4"
            >
              Together we can create empowered and sustainable communities in Kakamega and beyond. 
              Your support can transform lives and build a brighter future for all.
            </motion.p>
            
            {/* Fixed bottom buttons */}
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-3 sm:gap-6 justify-center items-center px-4 max-w-xs sm:max-w-none mx-auto"
            >
              <motion.div 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.95 }}
                className="w-full sm:w-auto"
              >
                <Link
                  to="/projects"
                  className="block w-full sm:w-auto bg-white text-primaryBlue px-6 md:px-10 py-3 md:py-4 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 shadow-2xl hover:shadow-3xl text-center text-sm md:text-base"
                >
                  <span className="flex items-center justify-center gap-2">
                    <FaHandHoldingHeart /> Our Projects
                  </span>
                </Link>
              </motion.div>
              
              <motion.div 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.95 }}
                className="w-full sm:w-auto"
              >
                <Link
                  to="/donate"
                  className="block w-full sm:w-auto bg-primaryGold text-white px-6 md:px-10 py-3 md:py-4 rounded-full font-semibold hover:bg-yellow-400 transition-all duration-300 shadow-2xl hover:shadow-3xl text-center text-sm md:text-base"
                >
                  <span className="flex items-center justify-center gap-2">
                    Support Our Work <FaArrowRight />
                  </span>
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default About;